import * as React from "react";
import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Pause, Play, Square } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { isArabicText, Message } from "@/types/chat";
import { useQuery } from "@tanstack/react-query";
import { translate, useTranslation } from "@/lib/translation-utils";

// Cache configuration
const CACHE_PREFIX = 'tts_cache_';
const CACHE_EXPIRY = 3 * 24 * 60 * 60 * 1000; // 3 days
const MAX_CACHE_SIZE = 25 * 1024 * 1024; // 25MB
const MIN_STORAGE_SPACE = 5 * 1024 * 1024; // 5MB minimum free space required

// Utility functions
const checkStorageAvailability = () => {
  try {
    const testKey = `${CACHE_PREFIX}_test`;
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.warn('[AudioPlayerDialog] localStorage not available:', error);
    return false;
  }
};

const getAvailableSpace = () => {
  try {
    let totalSize = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        totalSize += localStorage.getItem(key)?.length || 0;
      }
    }
    return Math.max(0, MAX_CACHE_SIZE - totalSize);
  } catch (error) {
    console.warn('[AudioPlayerDialog] Failed to calculate storage space:', error);
    return 0;
  }
};

const generateUniqueHash = (text: string, timestamp: string): string => {
  try {
    // Combine text and timestamp to create a unique identifier
    const uniqueString = `${text}_${timestamp}`;
    const encoder = new TextEncoder();
    const data = encoder.encode(uniqueString);

    // Create a simple hash
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      hash = ((hash << 5) - hash) + data[i];
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Convert to positive base36 string and take first 32 chars
    return Math.abs(hash).toString(36).slice(0, 32);
  } catch (error) {
    console.warn('[AudioPlayerDialog] Hash generation failed:', error);
    return Date.now().toString(36);
  }
};

const getCacheKey = (text: string, timestamp: string = Date.now().toString()): string => {
  try {
    const hash = generateUniqueHash(text, timestamp);
    return `${CACHE_PREFIX}${hash}`;
  } catch (error) {
    console.warn('[AudioPlayerDialog] Failed to generate cache key:', error);
    return `${CACHE_PREFIX}${Date.now()}`;
  }
};

const base64toBlob = (base64Data: string): Blob => {
  try {
    const byteString = atob(base64Data.split(',')[1]);
    const mimeString = base64Data.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mimeString });
  } catch (error) {
    console.error('[AudioPlayerDialog] Error converting base64 to blob:', error);
    throw new Error('Failed to process audio data');
  }
};

const cleanupCache = () => {
  try {
    const now = Date.now();
    const keysToRemove: string[] = [];
    let totalSize = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        try {
          const item = localStorage.getItem(key);
          if (item) {
            const { timestamp, data } = JSON.parse(item);
            const size = data.length || 0;

            if (now - Number(timestamp) > CACHE_EXPIRY || size > MAX_CACHE_SIZE) {
              keysToRemove.push(key);
            } else {
              totalSize += size;
            }
          }
        } catch (e) {
          console.warn('[AudioPlayerDialog] Cache entry parsing failed:', e);
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        totalSize -= (localStorage.getItem(key)?.length || 0);
      } catch (e) {
        console.warn('[AudioPlayerDialog] Failed to remove cache entry:', e);
      }
    });

    if (totalSize > MAX_CACHE_SIZE - MIN_STORAGE_SPACE) {
      const cacheEntries = Object.entries(localStorage)
        .filter(([key]) => key.startsWith(CACHE_PREFIX))
        .map(([key, value]) => ({
          key,
          timestamp: JSON.parse(value).timestamp,
          size: value.length
        }))
        .sort((a, b) => Number(a.timestamp) - Number(b.timestamp));

      for (const entry of cacheEntries) {
        try {
          localStorage.removeItem(entry.key);
          totalSize -= entry.size;
          if (totalSize <= MAX_CACHE_SIZE - MIN_STORAGE_SPACE) break;
        } catch (e) {
          console.warn('[AudioPlayerDialog] Failed to remove cache entry during cleanup:', e);
        }
      }
    }

    return totalSize <= MAX_CACHE_SIZE - MIN_STORAGE_SPACE;
  } catch (error) {
    console.error('[AudioPlayerDialog] Cache cleanup failed:', error);
    return false;
  }
};

const getWordCount = (text: string): number => {
  return text.trim().split(/\s+/).length;
};

const SUMMARY_CACHE_PREFIX = 'summary_cache_';
const getSummaryCacheKey = (text: string, timestamp: string = Date.now().toString()): string => {
  try {
    const hash = generateUniqueHash(text, timestamp);
    return `${SUMMARY_CACHE_PREFIX}${hash}`;
  } catch (error) {
    console.warn('[AudioPlayerDialog] Failed to generate summary cache key:', error);
    return `${SUMMARY_CACHE_PREFIX}${Date.now()}`;
  }
};


interface AudioPlayerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  message: Message;
}

export function AudioPlayerDialog({ isOpen, onClose, message }: AudioPlayerDialogProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const { t } = useTranslation(); // Add useTranslation hook to make component re-render on language change
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [displayText, setDisplayText] = useState(message.content);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isRTL, setIsRTL] = useState(false);
  const [cacheKey] = useState(() => getCacheKey(message.content, message.timestamp.toString()));
  const [summaryCacheKey] = useState(() => getSummaryCacheKey(message.content, message.timestamp.toString()));

  // Effect to update text direction and display text
  useEffect(() => {
    setIsRTL(isArabicText(message.content));
    setDisplayText(message.content);
  }, [message.content]);

  // Effect to handle dialog open state and auto-summarization
  useEffect(() => {
    if (isOpen) {
      const wordCount = getWordCount(message.content);
      if (wordCount > 20) {
        (async () => {
          try {
            setIsSummarizing(true);
            setError(null);
            const summarizedText = await getSummarizedText(message.content);
            setDisplayText(summarizedText);
          } catch (error) {
            console.error("[AudioPlayerDialog] Failed to process text:", error);
            setError(t("Failed to process text. Please try again."));
          } finally {
            setIsSummarizing(false);
          }
        })();
      }
    } else {
      setIsPlaying(false);
      setIsLoading(false);
      setError(null);
      setHasStartedPlaying(false);
      setIsSummarizing(false);

      if (audioRef.current) {
        const audio = audioRef.current;
        audio.onloadeddata = null;
        audio.onerror = null;
        audio.onended = null;
        audio.onpause = null;
        audio.onplay = null;
        audio.pause();
        audio.currentTime = 0;
      }

      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
    }
  }, [isOpen, message.content, audioUrl]);

  const getSummarizedText = async (originalText: string): Promise<string> => {
    // Check cache first
    try {
      const cachedSummary = localStorage.getItem(summaryCacheKey);
      if (cachedSummary) {
        const { timestamp: cachedTimestamp, data } = JSON.parse(cachedSummary);
        if (Date.now() - Number(cachedTimestamp) <= CACHE_EXPIRY) {
          console.log("[AudioPlayerDialog] Using cached summary");
          return data;
        } else {
          localStorage.removeItem(summaryCacheKey);
        }
      }
    } catch (error) {
      console.warn("[AudioPlayerDialog] Cache read failed:", error);
    }

    // If no cache, get new summary
    try {
      const response = await fetch("/api/summarize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: originalText })
      });

      if (!response.ok) {
        throw new Error(`Failed to summarize text: ${response.statusText}`);
      }

      const { summary } = await response.json();

      // Cache the summary
      try {
        cleanupCache();
        localStorage.setItem(summaryCacheKey, JSON.stringify({
          timestamp: Date.now(),
          data: summary
        }));
      } catch (error) {
        console.warn("[AudioPlayerDialog] Summary cache write failed:", error);
      }

      return summary;
    } catch (error) {
      console.error("[AudioPlayerDialog] Summarization failed:", error);
      throw error;
    }
  };

  const generateSpeech = async (textToSpeak: string) => {
    if (!textToSpeak) return;

    // Cleanup previous audio state
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }

    setIsLoading(true);
    setError(null);
    setIsPlaying(false);

    try {
      let audioBlob: Blob | null = null;

      // Try to get from cache using the unique key
      if (checkStorageAvailability()) {
        try {
          const cachedItem = localStorage.getItem(cacheKey);
          if (cachedItem) {
            const { timestamp: cachedTimestamp, data } = JSON.parse(cachedItem);
            if (Date.now() - Number(cachedTimestamp) <= CACHE_EXPIRY) {
              audioBlob = base64toBlob(data);
              console.log("[AudioPlayerDialog] Using cached audio for:", textToSpeak.substring(0, 30));
            } else {
              localStorage.removeItem(cacheKey);
            }
          }
        } catch (error) {
          console.warn("[AudioPlayerDialog] Cache read failed:", error);
        }
      }

      if (!audioBlob) {
        console.log("[AudioPlayerDialog] Requesting speech generation for:", textToSpeak.substring(0, 30));

        const response = await fetch("/api/text-to-speech", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text: textToSpeak,
            voiceId: "IK7YYZcSpmlkjKrQxbSn" // Raed voice ID
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to generate speech: ${response.statusText}`);
        }

        console.log("[AudioPlayerDialog] Successfully received audio response");
        audioBlob = await response.blob();

        if (checkStorageAvailability() && getAvailableSpace() >= MIN_STORAGE_SPACE) {
          const reader = new FileReader();
          reader.readAsDataURL(audioBlob);
          reader.onloadend = () => {
            try {
              cleanupCache();

              const data = reader.result as string;
              if (data.length <= MAX_CACHE_SIZE / 2) {
                localStorage.setItem(cacheKey, JSON.stringify({
                  timestamp: Date.now(),
                  data
                }));
                console.log("[AudioPlayerDialog] Successfully cached audio with key:", cacheKey);
              } else {
                console.warn("[AudioPlayerDialog] Audio too large to cache");
              }
            } catch (error) {
              console.warn("[AudioPlayerDialog] Cache write failed:", error);
            }
          };
        }
      }

      // Validate blob before creating URL
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error("Invalid audio data received");
      }

      // Verify the blob type is audio
      if (!audioBlob.type.startsWith('audio/')) {
        console.warn("[AudioPlayerDialog] Blob is not audio type:", audioBlob.type);
        // Try to convert if not audio type but we have data
        audioBlob = new Blob([audioBlob], { type: 'audio/mpeg' });
      }

      console.log("[AudioPlayerDialog] Audio blob details:", {
        type: audioBlob.type,
        size: audioBlob.size,
        timestamp: new Date().toISOString()
      });

      // Create and validate URL
      const newUrl = URL.createObjectURL(audioBlob);
      if (!newUrl) {
        throw new Error("Failed to create audio URL");
      }

      console.log("[AudioPlayerDialog] Created audio URL:", newUrl);
      setAudioUrl(newUrl);

      if (audioRef.current) {
        try {
          const audio = audioRef.current;

          // Clean up any existing event listeners
          audio.onloadeddata = null;
          audio.onerror = null;
          audio.onended = null;
          audio.onpause = null;
          audio.onplay = null;

          // Set up event handlers before setting src
          audio.onloadeddata = () => {
            console.log("[AudioPlayerDialog] Audio loaded successfully");
            setIsLoading(false);
            
            // Small delay to ensure audio is fully loaded before play
            setTimeout(() => {
              audio.play()
                .then(() => {
                  console.log("[AudioPlayerDialog] Auto-playback started successfully");
                  setIsPlaying(true);
                  setHasStartedPlaying(true);
                })
                .catch(error => {
                  console.error("[AudioPlayerDialog] Auto-playback failed:", error);
                  setError(t("Failed to auto-play audio. Click play to try again."));
                  setIsPlaying(false);
                });
            }, 100);
          };

          audio.onerror = (e) => {
            console.error("[AudioPlayerDialog] Audio loading error:", e, audio.error);
            const audioElement = audio;
            let errorMessage = t("Failed to load audio. Please try again.");

            if (audioElement.error) {
              switch (audioElement.error.code) {
                case 1:
                  errorMessage = t("Audio playback was aborted.");
                  break;
                case 2:
                  errorMessage = t("Network error occurred while loading audio.");
                  break;
                case 3:
                  errorMessage = t("Audio decoding failed.");
                  break;
                case 4:
                  errorMessage = t("Audio format not supported.");
                  break;
              }
            }

            setError(errorMessage);
            setIsLoading(false);
          };

          // Set src and load after setting up handlers
          audio.src = newUrl;
          audio.load();

        } catch (error) {
          console.error("[AudioPlayerDialog] Error setting up audio:", error);
          setError(t("Failed to setup audio playback"));
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error("[AudioPlayerDialog] Error generating speech:", error);
      setError(error instanceof Error ? error.message : t("Failed to generate speech"));
      setIsLoading(false);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setHasStartedPlaying(false);
    }
  };

  const togglePlayPause = async () => {
    if (!audioUrl && !isLoading) {
      // First time - generate speech
      setError(null);
      try {
        console.log("[AudioPlayerDialog] Starting speech generation for new audio");
        await generateSpeech(displayText);
      } catch (error) {
        console.error("[AudioPlayerDialog] Failed to generate speech:", error);
        setError(t("Failed to generate audio. Please try again."));
        return;
      }
      setHasStartedPlaying(true);
    } else if (audioRef.current) {
      // Audio already exists - toggle play/pause
      if (isPlaying) {
        console.log("[AudioPlayerDialog] Pausing audio");
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        try {
          console.log("[AudioPlayerDialog] Attempting to play audio");
          // Reset audio if it ended
          if (audioRef.current.ended) {
            audioRef.current.currentTime = 0;
          }
          
          // Try playing with retry
          let playAttempts = 0;
          const maxAttempts = 3;
          
          const attemptPlay = async () => {
            try {
              playAttempts++;
              await audioRef.current!.play();
              console.log("[AudioPlayerDialog] Play succeeded on attempt", playAttempts);
              setIsPlaying(true);
              setHasStartedPlaying(true);
              setError(null);
            } catch (playError) {
              console.error(`[AudioPlayerDialog] Play attempt ${playAttempts} failed:`, playError);
              
              if (playAttempts < maxAttempts) {
                console.log(`[AudioPlayerDialog] Retrying play, attempt ${playAttempts + 1}/${maxAttempts}`);
                setTimeout(attemptPlay, 300);
              } else {
                console.error("[AudioPlayerDialog] All play attempts failed");
                setError(t("Failed to play audio. Please try again."));
                setIsPlaying(false);
                
                // Last resort: try recreating the audio element
                if (audioUrl) {
                  console.log("[AudioPlayerDialog] Attempting to recreate audio element");
                  if (audioRef.current) {
                    audioRef.current.src = audioUrl;
                    audioRef.current.load();
                  }
                }
              }
            }
          };
          
          await attemptPlay();
        } catch (error) {
          console.error("[AudioPlayerDialog] Playback setup failed:", error);
          setError(t("Failed to play audio. Please try again."));
          setIsPlaying(false);
        }
      }
    }
  };

  // Get direction based on RTL status
  const direction = isRTL ? 'rtl' : 'ltr';
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" dir={direction}>
        <DialogHeader className="text-center">
          <DialogTitle className="text-lg font-semibold text-foreground text-center">
            {t("AI Summary")}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground text-center">
            {t("Listen to the summarized response using our AI Service.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative rounded-lg border p-3 overflow-y-auto">
            {isSummarizing || isLoading ? (
              <motion.div
                className="flex flex-col items-center justify-center gap-3 py-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {/* Enhanced loading dots - larger and more consistent with app style */}
                <div className="loading-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
                <span className="text-sm text-muted-foreground mt-2">
                  {isSummarizing ? t("Generating summary...") : t("Preparing audio...")}
                </span>
              </motion.div>
            ) : (
              <p
                className="text-sm text-muted-foreground"
                style={{
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                  unicodeBidi: 'embed'
                }}
              >
                {displayText}
              </p>
            )}
          </div>

          <div className="flex justify-center gap-2">
            {error ? (
              <p className="text-sm text-destructive" style={{ direction, textAlign: 'center' }}>
                {error}
              </p>
            ) : (
              <div className="flex items-center gap-2 justify-center" style={{ minWidth: '140px', minHeight: '48px' }}>
                {!isSummarizing && (
                  <Button
                    onClick={togglePlayPause}
                    size="icon"
                    className={cn(
                      "h-12 w-12 rounded-full",
                      isPlaying && "bg-primary/90 hover:bg-primary/80"
                    )}
                    disabled={isLoading || isSummarizing}
                  >
                    {isPlaying ? (
                      <Pause className="h-6 w-6" />
                    ) : (
                      <Play className={cn("h-6 w-6", isRTL ? "mr-1" : "ml-1")} />
                    )}
                  </Button>
                )}
                <AnimatePresence mode="wait">
                  {hasStartedPlaying && !isSummarizing && (
                    <motion.div
                      initial={{ width: 0, scale: 0, opacity: 0 }}
                      animate={{ width: "48px", scale: 1, opacity: 1 }}
                      exit={{ width: 0, scale: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                      style={{ position: 'relative', overflow: 'hidden' }}
                    >
                      <Button
                        onClick={handleStop}
                        size="icon"
                        className="h-12 w-12 rounded-full"
                        disabled={isLoading || isSummarizing}
                      >
                        <Square className="h-5 w-5" />
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
          <audio
            ref={audioRef}
            onEnded={() => {
              setIsPlaying(false);
              setHasStartedPlaying(false);
            }}
            onPause={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}